import React, { useEffect, useState } from "react";
import axios from "axios";
import { Shirt, Palette, PenLine, Ruler, Flag } from "lucide-react";
import { API, useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

/**
 * AddProductRouter — single-tier "Add Product" selector (Feb 2026 v3).
 *
 * The catalog is now UNIVERSAL (one Mongo-mirrored catalog covers every
 * vendor). We no longer need the "Purchasing Center → pick supplier"
 * branch. The router now shows three straightforward options:
 *
 *   { kind: "internal_catalog" }    → CatalogPicker (universal catalog)
 *   { kind: "internal_custom" }     → blank manual item
 *   { kind: "internal_service" }    → ServiceItemForm
 *
 * All buttons are type="button" so the surrounding <form> never validates
 * mid-flow.
 */
export default function AddProductRouter({ open, onClose, onPick }) {
  const { t } = useI18n();
  const { isPartner } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/catalog/sources`, {
          withCredentials: true,
        });
        const groups = data?.groups || [];
        const internal = groups.find((g) => g.key === "internal");
        // Sprint · Feb 2026 — Añadido client-side (aditivo, sin tocar el API
        // `/api/catalog/sources`): tile "Productos Personalizados" para
        // consulta del Product Catalog interno desde Create Estimate.
        // Sprint P2 · Cambio de diseño 1 — sólo se muestran "Ropa y
        // Uniformes" (internal_catalog) y "Productos Personalizados"
        // (internal_custom_catalog). Las opciones internal_custom e
        // internal_service se ocultan de la UI (endpoints intactos).
        const list = (internal?.sources || []).filter(
          (s) => s.kind === "internal_catalog"
        );
        list.push({ kind: "internal_custom_catalog", key: "custom_catalog" });
        // FASE 2 · Jun 2026 — tile "Producto Manual": línea ad-hoc que NO se
        // guarda en ningún catálogo (aditivo, client-side only).
        // Bloque 1/2 Partner Points — los partners ven el catálogo autorizado:
        // sin Producto Manual; Vinyl y Banner habilitados desde el Bloque 2
        // (validación segura server-side en el guardado).
        if (!isPartner) {
          list.push({ kind: "manual_item", key: "manual_item" });
        }
        // VINYL CALCULATOR · Jun 2026 — tile client-side aislado (sin backend).
        list.push({ kind: "vinyl_calculator", key: "vinyl_calculator" });
        // BANNER CALCULATOR · Jun 2026 — tile client-side aislado (sin backend).
        list.push({ kind: "banner_calculator", key: "banner_calculator" });
        setSources(list);
      } catch (_err) {
        setSources([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, isPartner]);

  if (!open) return null;

  return (
    <div
      data-testid="add-product-router-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="add-product-router-modal"
        className="bg-white w-full max-w-4xl max-h-[92vh] overflow-auto border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-black flex items-start justify-between gap-3">
          <div>
            <div className="label-overline">GP / ADD PRODUCT</div>
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">
              {t("add_product_router_title")}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {t("add_product_router_subtitle")}
            </p>
          </div>
          <button
            type="button"
            data-testid="add-product-router-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white"
          >
            ✕
          </button>
        </div>

        {loading && (
          <div className="p-8 text-sm text-[var(--text-secondary)]">
            Loading...
          </div>
        )}

        {!loading && (
          <InternalSourceGrid
            sources={sources}
            onPick={(kind) => onPick({ kind })}
            t={t}
          />
        )}
      </div>
    </div>
  );
}

function InternalSourceGrid({ sources, onPick, t }) {
  const meta = {
    internal_catalog: {
      lucide: Shirt,
      accent: "border-l-[#0E5FFF]",
      title: t("add_item_from_catalog_title"),
      desc: t("add_item_from_catalog_desc"),
      testId: "add-item-option-catalog",
    },
    internal_custom_catalog: {
      lucide: Palette,
      accent: "border-l-[#7C3AED]",
      title: t("add_item_custom_catalog_title"),
      desc: t("add_item_custom_catalog_desc"),
      testId: "add-item-option-custom-catalog",
    },
    manual_item: {
      lucide: PenLine,
      accent: "border-l-[#0F9D58]",
      title: t("add_item_manual_title"),
      desc: t("add_item_manual_desc"),
      testId: "add-item-option-manual",
    },
    vinyl_calculator: {
      lucide: Ruler,
      accent: "border-l-[#E11D48]",
      title: t("add_item_vinyl_title"),
      desc: t("add_item_vinyl_desc"),
      testId: "add-item-option-vinyl",
    },
    banner_calculator: {
      lucide: Flag,
      accent: "border-l-[#0D9488]",
      title: t("add_item_banner_title"),
      desc: t("add_item_banner_desc"),
      testId: "add-item-option-banner",
    },
    internal_custom: {
      icon: "🛠",
      accent: "border-l-black",
      title: t("add_item_custom_title"),
      desc: t("add_item_custom_desc"),
      testId: "add-item-option-custom",
    },
    internal_service: {
      icon: "⚙",
      accent: "border-l-[#FF6B00]",
      title: t("add_item_service_title"),
      desc: t("add_item_service_desc"),
      testId: "add-item-option-service",
    },
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--border)]">
      {sources.map((s) => {
        const m = meta[s.kind] || {};
        return (
          <button
            key={s.key}
            type="button"
            data-testid={m.testId || `add-item-option-${s.key}`}
            onClick={() => onPick(s.kind)}
            className={`group text-left bg-white p-6 min-h-[220px] hover:bg-[var(--bg)] transition-colors border-l-4 ${m.accent || "border-l-black"}`}
          >
            {m.lucide ? (
              <div className="w-12 h-12 mb-3 bg-black text-white flex items-center justify-center rounded-md group-hover:bg-[var(--primary,#e5007d)] transition-colors">
                <m.lucide size={24} strokeWidth={1.75} />
              </div>
            ) : (
              <div className="text-4xl mb-3">{m.icon || "📦"}</div>
            )}
            <div className="font-display text-lg sm:text-xl font-bold tracking-tight">
              {m.title || s.label}
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
              {m.desc || ""}
            </p>
            <div className="mt-4 font-mono-tech text-xs font-bold tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
              {t("add_item_picker_select")} ↗
            </div>
          </button>
        );
      })}
    </div>
  );
}
